import HomeIcon from '@mui/icons-material/Home'
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid'
import React from 'react'
import { useHistory } from 'react-router-dom'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useGetSolarProjectsQuery } from '../../../../api-services/solar/solarApi'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { Typography, Paper, Box, Button, Switch } from '@mui/material'
import { ProjectStatusChip } from '../../../components/chips/ProjectStatusChip'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import {
  selectShowInactivePlants,
  selectCurrentSolarProjectId,
  selectSolarPlantListPage,
  setShowInactivePlants, setSolarPlantListPage
} from './editSolarPlantSlice'
import { StartOutlined } from '@mui/icons-material'

const SolarPlantsPage: React.FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const currentSolarProjectId = useAppSelector<number | null>(selectCurrentSolarProjectId)
  const page: number = useAppSelector<number>(selectSolarPlantListPage)
  const showInactiveProjects: boolean = useAppSelector<boolean>(selectShowInactivePlants)

  const getProjectsApiResult = useGetSolarProjectsQuery({ showInactiveProjects })

  const goTo = (location: string): void => {
    history.push(location)
  }

  const handleGoToEdit = (targetSolarProjectId: string | undefined): void => {
    const target = targetSolarProjectId ?? '0'
    goTo(`/app/solar-engineering/solar-plants/${parseInt(target)}/edit`)
  }

  const handleChangePage = (newPage: number): void => {
    dispatch(setSolarPlantListPage(newPage))
  }

  const handleProjectFilterSwitch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!event.target.checked) {
      dispatch(setShowInactivePlants(false))
    } else if (event.target.checked) {
      dispatch(setShowInactivePlants(true))
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'projectId',
      headerName: 'Project ID',
      flex: 15,
      valueGetter: (params) => params.row.identifier.projectId
    },
    {
      field: 'projectName',
      headerName: 'Project name',
      flex: 50,
      valueGetter: (params) => params.row.identifier.projectName
    },
    {
      field: 'identifier.solarProjectId',
      headerName: 'Solar Project ID',
      flex: 15,
      valueGetter: (params) => params.row.identifier.solarProjectId
    },
    {
      field: 'projectStatus',
      headerName: 'Status',
      flex: 20,
      renderCell: params => (
        <ProjectStatusChip projectStatus={params.row.summary.projectStatus}/>
      )
    },
    {
      field: 'summary.kwpOutput',
      headerName: 'kWp',
      flex: 20,
      headerAlign: 'center',
      align: 'right',
      valueGetter: (params) => params.row.summary.kwpOutput
    },
    {
      field: 'summary.batteryNameplateCapacityKwh',
      headerName: 'Battery Nameplate kWh',
      flex: 30,
      headerAlign: 'center',
      align: 'right',
      valueGetter: (params) => params.row.summary.batteryNameplateCapacityKwh
    },
    {
      field: 'earlyEnergyDateFormatted',
      headerName: 'Early Energy',
      flex: 30,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.summary.earlyEnergyDateFormatted
    },
    {
      field: 'summary.gridOutageTrackingEnabled',
      headerName: 'Grid Outage Tracking',
      flex: 30,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.summary.gridOutageTrackingEnabled.toString() === 'true' ? 'Y' : ''
    },
    {
      field: 'assetLeaseCommercialOperationDateFormatted',
      headerName: 'COD',
      flex: 30,
      headerAlign: 'center',
      align: 'center',
      valueGetter: (params) => params.row.summary.assetLeaseCommercialOperationDateFormatted
    },
    {
      field: 'editSolarPlantId', // Don't repeat ID - does weird things with the ordering of the columns
      headerName: '',
      width: 100,
      valueGetter: (params) => params.row.identifier.solarProjectId,
      renderCell: (params: GridRenderCellParams<string>) => (
        <Button onClick={() => {
          handleGoToEdit(params.value)
        }} type={'button'} variant={'text'}>
          <StartOutlined />
        </Button>
      )
    }
  ]

  return (
    <>
      <Box sx={{ mb: 4, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>

        <Typography variant={'h4'}>Solar Plants</Typography>
        <Button variant={'text'} type="button" onClick={() => { goTo('/app/solar-engineering-area') }}>
          <HomeIcon style={{ color: '#444' }}/>
        </Button>
      </Box>
      <Paper>
        <Box sx={{ p: 4, mt: 3 }}>
          {getProjectsApiResult.isLoading && <LoadingComponent/>}
          {getProjectsApiResult.isError &&
            <ErrorComponent message={RtkQueryHelper.mapResponseError(getProjectsApiResult.error)}/>}
          {getProjectsApiResult.isSuccess && (
            <DataGrid
              rows={getProjectsApiResult.data}
              getRowId={(row) => row.identifier.solarProjectId}
              columns={columns}
              autoHeight={true}
              disableSelectionOnClick
              getRowClassName={params => {
                return currentSolarProjectId === params.row.solarProjectId ? 'Mui-selected' : ''
              }}

              pagination
              pageSize={15}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPageOptions={[15]}
            />
          )}
        </Box>

        <Box sx={{ pr: 4, pb: 4, pt: 0, textAlign: 'right' }}>
          Show Inactive Projects
          <Switch
            checked={showInactiveProjects}
            onChange={handleProjectFilterSwitch}
            color="primary"
          />
        </Box>
      </Paper>
    </>
  )
}

export default SolarPlantsPage
